import { zodResolver } from '@hookform/resolvers/zod';
import { normaliseState, siteBody } from '@pulsepoint/shared';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { Map as MapIcon, MapPin } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { useRateSchedules } from '../../api/chargers';
import { useCreateSite, useUpdateSite, type Site } from '../../api/organisations';
import { usePermission } from '../../auth/permissions';
import { useDialog, type DialogProps } from '../../components/dialogs/DialogProvider';
import { FormDialog } from '../../components/dialogs/FormDialog';
import { applyApiError } from '../../components/form/apiErrors';
import { FieldRow } from '../../components/form/FieldRow';
import { FormSelect } from '../../components/form/FormSelect';
import { FormTextField } from '../../components/form/FormTextField';
import { MapPickerDialog, pointOf } from '../../components/map/MapPickerDialog';
import { useToast } from '../../components/Toast';
import { formSaver, useUnsavedChanges } from '../../components/unsaved/unsaved';
import { findLocation, MAPTILER_KEY } from '../../lib/maptiler';
import { stateOptions, timezoneOptions } from './options';

type In = z.input<typeof siteBody>;
type Out = z.output<typeof siteBody>;
const FIELDS = ['name', 'addressStreet', 'addressSuburb', 'addressState', 'addressPostcode', 'latitude', 'longitude', 'timezone', 'rateScheduleId', 'notes'];

/** Add a site to an organisation, or edit one. */
export function SiteDialog({
  organisationId,
  organisationName,
  organisationTimezone,
  site,
  readOnly,
  close,
}: {
  organisationId: string;
  organisationName: string;
  organisationTimezone: string;
  site?: Site;
  readOnly?: boolean;
} & DialogProps<Site>) {
  const create = useCreateSite();
  const update = useUpdateSite();
  const toast = useToast();
  const dialog = useDialog();
  const [error, setError] = useState('');
  // Peak and off-peak hours are part of pricing: admins choose them.
  const canPrice = usePermission('tariffs.edit');
  const schedules = useRateSchedules(canPrice);
  const scheduleOptions = (schedules.data ?? []).map((r) => ({ value: r.id, label: `${r.name} (${r.periods.map((p) => p.name).join(', ')})` }));
  const form = useForm<In, unknown, Out>({
    resolver: zodResolver(siteBody),
    disabled: readOnly,
    defaultValues: {
      name: site?.name ?? '',
      addressStreet: site?.addressStreet ?? '',
      addressSuburb: site?.addressSuburb ?? '',
      addressState: normaliseState(site?.addressState) ?? '',
      addressPostcode: site?.addressPostcode ?? '',
      latitude: site?.latitude ?? '',
      longitude: site?.longitude ?? '',
      timezone: (site?.timezone ?? organisationTimezone) as In['timezone'],
      rateScheduleId: site?.rateScheduleId ?? '',
      notes: site?.notes ?? '',
    } as In,
  });

  // The site's location from its address (MapTiler), to check and adjust before saving.
  const [finding, setFinding] = useState(false);
  const [found, setFound] = useState<string | null>(null);
  const locate = async () => {
    const v = form.getValues();
    const address = [v.addressStreet, v.addressSuburb, v.addressState, v.addressPostcode].filter(Boolean).join(', ');
    setFound(null);
    if (!address) {
      setFound('Enter the address first');
      return;
    }
    setFinding(true);
    try {
      const r = await findLocation(address);
      if (!r) {
        setFound('No match: check the address, or enter the location by hand');
        return;
      }
      form.setValue('latitude', r.latitude.toFixed(6), { shouldDirty: true, shouldValidate: true });
      form.setValue('longitude', r.longitude.toFixed(6), { shouldDirty: true, shouldValidate: true });
      setFound(`Found: ${r.place}`);
    } catch (e) {
      setFound((e as Error).message);
    } finally {
      setFinding(false);
    }
  };

  // Or click the spot on a map (it starts on the current location, if there is one).
  const pickOnMap = async () => {
    const v = form.getValues();
    const picked = await dialog.open(MapPickerDialog, { title: `Where ${v.name || 'the site'} is`, value: pointOf(v.latitude, v.longitude), centre: null });
    if (!picked) return;
    form.setValue('latitude', picked.latitude, { shouldDirty: true, shouldValidate: true });
    form.setValue('longitude', picked.longitude, { shouldDirty: true, shouldValidate: true });
    setFound(null);
  };

  const submit = formSaver(form, async (v) => {
    setError('');
    try {
      const { rateScheduleId, ...rest } = v;
      const body = canPrice ? v : rest;
      const saved = site ? await update.mutateAsync({ id: site.id, ...body }) : await create.mutateAsync({ organisationId, ...body });
      toast(`${saved.name} was saved.`);
      close(saved);
    } catch (e) {
      setError(applyApiError(e, form.setError, FIELDS));
      return false;
    }
  });
  useUnsavedChanges({ dirty: form.formState.isDirty, save: submit, discard: () => form.reset() });

  return (
    <FormDialog
      title={site ? (readOnly ? site.name : `Edit ${site.name}`) : 'Add a site'}
      lead={site ? organisationName : `A place where ${organisationName} has chargers: an office car park, a building, a depot.`}
      submitLabel={site ? 'Save changes' : 'Add the site'}
      onSubmit={submit}
      onClose={() => close()}
      busy={create.isPending || update.isPending}
      error={error}
      readOnly={readOnly}
    >
      <FormTextField control={form.control} name="name" label="Name" placeholder="e.g. Richmond office" autoFocus={!readOnly} />
      <FormTextField control={form.control} name="addressStreet" label="Street address" />
      <FieldRow columns="2fr 1fr 1fr">
        <FormTextField control={form.control} name="addressSuburb" label="Suburb" />
        <FormSelect control={form.control} name="addressState" label="State" options={stateOptions} emptyLabel="Not set" />
        <FormTextField control={form.control} name="addressPostcode" label="Postcode" slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 4 } }} />
      </FieldRow>
      <FieldRow>
        <FormTextField control={form.control} name="latitude" label="Latitude" placeholder="-37.8183" helperText={found ?? 'Puts the site on the map'} slotProps={{ htmlInput: { inputMode: 'decimal' } }} />
        <FormTextField control={form.control} name="longitude" label="Longitude" placeholder="144.9980" slotProps={{ htmlInput: { inputMode: 'decimal' } }} />
      </FieldRow>
      {!readOnly && MAPTILER_KEY && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: -1 }}>
          <Button startIcon={<MapPin size={16} strokeWidth={1.75} />} onClick={() => void locate()} disabled={finding}>
            {finding ? 'Finding…' : 'Find from the address'}
          </Button>
          <Button startIcon={<MapIcon size={16} strokeWidth={1.75} />} onClick={() => void pickOnMap()}>
            Pick on the map
          </Button>
        </Box>
      )}
      <FieldRow>
        <FormSelect control={form.control} name="timezone" label="Time zone" options={timezoneOptions} helperText="Peak and off-peak hours follow this clock" />
        {canPrice ? (
          <FormSelect control={form.control} name="rateScheduleId" label="Peak and off-peak hours" options={scheduleOptions} emptyLabel="One price all day" helperText="Set them up under Settings" />
        ) : (
          site?.rateScheduleName && <TextField label="Peak and off-peak hours" value={site.rateScheduleName} disabled />
        )}
      </FieldRow>
      <FormTextField control={form.control} name="notes" label="Notes" multiline minRows={2} />
    </FormDialog>
  );
}
